// Contracts for roulette: zod schemas for inputs, socket events, public state types.
import { z } from 'zod';
import type { ApiError } from '../errors';
import { zAmount, zIdempotencyKey } from '../common';

export const ROULETTE_BET_COLORS = ['red', 'green', 'black'] as const;
export type RouletteBetColor = (typeof ROULETTE_BET_COLORS)[number];
export const zRouletteColor = z.enum(ROULETTE_BET_COLORS);

export const ROULETTE_PHASES = ['bets', 'spinning', 'result'] as const;
export type RoulettePhase = (typeof ROULETTE_PHASES)[number];

/** POST /api/roulette/bet — one colour per request; several colours in a round = several requests (own keys). */
export const zRouletteBet = z
  .object({
    /** round the client sees; if it is not the current open round → ROUND_ALREADY_STARTED */
    roundId: z.string().uuid().optional(),
    color: zRouletteColor,
    amount: zAmount,
    idempotencyKey: zIdempotencyKey,
  })
  .strict();
export type RouletteBetInput = z.input<typeof zRouletteBet>;

/** A bet as everyone in the room sees it. */
export interface RouletteBetView {
  id: string;
  /** User.publicId — the client compares it with `me.publicId` to mark "You" */
  playerId: number;
  nickname: string;
  avatar: string;
  color: RouletteBetColor;
  amount: number;
  /** null until the round is settled; 0 = lost */
  payout: number | null;
}

/** Where the strip stops: server-computed, the client only animates to it. */
export interface RouletteSpin {
  /** winning cell index in the 15-cell layout */
  cell: number;
  color: RouletteBetColor;
  multiplier: number;
  /** stop point inside the cell, fraction of the cell width from its centre */
  offset: number;
}

export interface RouletteHistoryItem {
  roundId: string;
  roundNo: number;
  cell: number;
  color: RouletteBetColor;
}

export interface RouletteTimings {
  betMs: number;
  spinMs: number;
  resultMs: number;
}

/** GET /api/roulette/state, ack of `roulette:join`, and the `roulette:state` push. */
export interface RouletteState {
  phase: RoulettePhase;
  roundId: string;
  /** "#90412" */
  roundNo: number;
  serverSeedHash: string;
  /** revealed in phase `result` */
  serverSeed: string | null;
  /** clientSeed of the shared round (`round:<roundId>`), nonce is always 0 */
  clientSeed: string;
  /** epoch ms */
  phaseStartedAt: number;
  phaseEndsAt: number;
  /** server clock at send time (epoch ms) — client computes its clock offset */
  serverNow: number;
  timings: RouletteTimings;
  /** strip colours, index = cell */
  layout: RouletteBetColor[];
  payouts: Record<RouletteBetColor, number>;
  minBet: number;
  maxBet: number;
  bets: RouletteBetView[];
  /** null while taking bets */
  spin: RouletteSpin | null;
  /** last ≤100 outcomes, newest first; the current spin is added in phase `result` */
  history: RouletteHistoryItem[];
}

/** POST /api/roulette/bet response */
export interface RouletteBetResult {
  bet: RouletteBetView;
  roundId: string;
  balance: number;
  /** true: this idempotencyKey was already used, nothing was debited now */
  duplicate: boolean;
}

/** GET /api/roulette/history */
export interface RouletteHistoryResponse {
  history: RouletteHistoryItem[];
}

export const ROULETTE_HISTORY_LIMIT = 100;

export interface RouletteServerToClient {
  /** full state on every phase change */
  'roulette:state': (s: RouletteState) => void;
  /** a new bet in the current round */
  'roulette:bet': (e: { roundId: string; bet: RouletteBetView }) => void;
}

export interface RouletteClientToServer {
  /** joins the roulette room and returns the current state (with history) */
  'roulette:join': (ack: (r: RouletteState | ApiError) => void) => void;
  'roulette:leave': () => void;
}

export interface RouletteColumnPlayer {
  playerId: number;
  nickname: string;
  avatar: string;
  amount: number;
  /** null until settled */
  payout: number | null;
}

export interface RouletteColumn {
  color: RouletteBetColor;
  total: number;
  players: RouletteColumnPlayer[];
}

/**
 * Groups bets into the Red / Green / Black columns: one row per player per colour (amounts summed),
 * rows sorted by amount desc, then by nickname.
 */
export function rouletteColumns(bets: readonly RouletteBetView[]): Record<RouletteBetColor, RouletteColumn> {
  const out = {} as Record<RouletteBetColor, RouletteColumn>;
  for (const color of ROULETTE_BET_COLORS) {
    const byPlayer = new Map<number, RouletteColumnPlayer>();
    let total = 0;
    for (const b of bets) {
      if (b.color !== color) continue;
      total += b.amount;
      const row = byPlayer.get(b.playerId);
      if (row) {
        row.amount += b.amount;
        row.payout = row.payout === null || b.payout === null ? null : row.payout + b.payout;
      } else {
        byPlayer.set(b.playerId, { playerId: b.playerId, nickname: b.nickname, avatar: b.avatar, amount: b.amount, payout: b.payout });
      }
    }
    const players = [...byPlayer.values()].sort((a, b) => b.amount - a.amount || a.nickname.localeCompare(b.nickname));
    out[color] = { color, total, players };
  }
  return out;
}
